import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, UserPlus, CalendarCheck, Share2, Facebook, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const CardButton = ({ icon: Icon, title, sub, onClick, href, download, primary, testId }) => {
  const cls = `group w-full flex items-center gap-4 border px-6 py-4 transition-colors duration-300 ${
    primary
      ? "bg-gold border-gold text-black hover:bg-white hover:border-white"
      : "border-white/15 text-white hover:border-gold"
  }`;
  const inner = (
    <>
      <span className={`w-10 h-10 shrink-0 flex items-center justify-center border ${primary ? "border-black/25" : "border-gold/40 text-gold"}`}>
        <Icon className="w-4.5 h-4.5" strokeWidth={1.5} style={{ width: 18, height: 18 }} />
      </span>
      <span className="flex-1 text-left">
        <span className="block text-sm font-medium tracking-wide">{title}</span>
        <span className={`block text-[11px] font-light mt-0.5 ${primary ? "text-black/60" : "text-neutral-500"}`}>{sub}</span>
      </span>
      <ArrowUpRight className={`w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${primary ? "text-black" : "text-gold"}`} />
    </>
  );
  if (href) {
    return (
      <a href={href} download={download} target={download ? undefined : "_blank"} rel="noopener noreferrer" className={cls} data-testid={testId}>
        {inner}
      </a>
    );
  }
  return (
    <button onClick={onClick} className={cls} data-testid={testId}>
      {inner}
    </button>
  );
};

export default function Card() {
  const navigate = useNavigate();

  const share = async () => {
    const data = { title: "Elevated Apex Co.", text: "Credit education & financial elevation — book a free consultation.", url: window.location.href };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch (e) {
        // user dismissed
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Card link copied — paste it anywhere.");
    }
  };

  const goBook = () => {
    navigate("/");
    setTimeout(() => scrollToSection("contact"), 900);
  };

  return (
    <main className="grain bg-ink-base min-h-screen flex items-center justify-center px-5 py-14" data-testid="card-page">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="w-full max-w-sm border border-gold/30 bg-ink-surface px-8 py-12 text-center shadow-[0_40px_120px_-30px_rgba(212,175,55,0.15)]"
      >
        <motion.span
          initial={{ rotate: 45, scale: 0 }}
          animate={{ rotate: 45, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mx-auto w-14 h-14 border border-gold flex items-center justify-center"
        >
          <span className="w-3 h-3 bg-gold" />
        </motion.span>

        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.35em] text-gold" data-testid="card-overline">
          Credit Education & Financial Elevation
        </p>
        <h1 className="mt-4 font-serif text-3xl text-white tracking-tight" data-testid="card-brand">
          Elevated <span className="italic text-gold">Apex</span> Co.
        </h1>
        <p className="mt-3 text-xs text-neutral-500 font-light leading-relaxed">
          Your score is not your ceiling. It's your launchpad.
        </p>

        <div className="my-8 h-px bg-white/10" />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.08, delayChildren: 0.4 } } }}
          className="space-y-3"
        >
          {[
            <CardButton key="book" primary icon={CalendarCheck} title="Book a Free Consultation" sub="30 minutes, zero obligation" onClick={goBook} testId="card-book-button" />,
            <CardButton key="email" icon={Mail} title="Email Us" sub="Info@elevatedapexco.com" href="mailto:Info@elevatedapexco.com" testId="card-email-button" />,
            <CardButton key="save" icon={UserPlus} title="Save My Contact" sub="One tap — straight to your phone" href="/elevated-apex-co.vcf" download testId="card-save-contact-button" />,
            <CardButton key="share" icon={Share2} title="Share This Card" sub="Send it to someone who needs it" onClick={share} testId="card-share-button" />,
            <CardButton key="fb" icon={Facebook} title="Follow on Facebook" sub="Tips, education & client wins" href="https://www.facebook.com/share/1KAxAG7G3j/" testId="card-facebook-button" />,
          ].map((el, i) => (
            <motion.div
              key={i}
              variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } }}
            >
              {el}
            </motion.div>
          ))}
        </motion.div>

        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600" data-testid="card-url">
          elevatedapexco.com
        </p>
      </motion.div>
    </main>
  );
}
